import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '../firebase/translate.service';
import { Program, Language } from '../Models/program.model';
import { Organization } from '../organizations/organization';
import { Category, ProviderCategory, Subcategory } from '../categories/category';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FirebaseAuthService } from '../firebase/firebaseAuth.service';
import { Timestamp } from './timestamp';
import { GeocodeService } from '../Services/geocode.service';
import { ProgramService } from '../Services/program.service';
import { Coordinate } from '../Models/coordinate.model';
import { CategoryService } from '../Services/category.service';
import { SubcategoryService } from '../Services/subcategory.service';
import { OrganizationService } from '../Services/organization.service';
import { ProviderCategoryService } from 'app/Services/provider-category.service';
import {
  categoryIdsOf,
  flattenSubcategories,
  legacyMirror,
  pruneSubcategories,
  subcategoriesByCategoryOf,
} from './multi-category';

@Component({
  selector: 'app-edit-program',
  templateUrl: './editProgram.component.html',
  styleUrls: ['./editProgram.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class EditProgramComponent implements OnInit {
  program: Program;

  organizations: Organization[];
  selectedOrganizationId: string;

  categories: Category[];
  selectedCategoryIds: string[] = [];

  // Selected subcategory ids per category, and the available subcategory options for each category.
  subcategoriesByCategory: { [categoryId: string]: string[] } = {};
  subcategoryOptionsByCategory: { [categoryId: string]: Subcategory[] } = {};

  allProviderCategories: ProviderCategory[] = [];
  providerCategories: ProviderCategory[] = [];
  selectedProviderCategories: string[] = [];

  coordinates: Coordinate;
  address: string;
  call: string;
  email: string;
  website: string;
  translations: Language[] = [];

  translatedPrograms = {};
  languages: string[];

  message: string;
  error = false;

  id: string;
  fromCat = false;

  timestamp: Timestamp;

  constructor(
    private auth: FirebaseAuthService,
    private categoryAccessor: CategoryService,
    private geocoder: GeocodeService,
    private location: Location,
    private organizationAccessor: OrganizationService,
    private programAccessor: ProgramService,
    private providerCategoryAccessor: ProviderCategoryService,
    private route: ActivatedRoute,
    private router: Router,
    private subcategoryAccessor: SubcategoryService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.fromCat = params['fromCat'];
    });

    this.program = await this.programAccessor
      .getProgramById(this.id)
      .take(1)
      .toPromise();
    this.languages = await this.translate.parseLanguages();

    for (const language of this.languages) {
      this.translatedPrograms[
        language
      ] = await this.programAccessor.getSingleProgram(this.id, language);
      if (!this.translatedPrograms[language]) {
        // If program doesn't exist in language, set display values for edit page
        this.translatedPrograms[language] = {
          Summary: '',
          Description: '',
          userUpdated: false,
        };
      }
    }

    this.categoryAccessor
      .getUpdatableCategories()
      .snapshotChanges()
      .subscribe((snapshots) => {
        this.categories = [];
        snapshots.forEach((snapshot) => {
          const category: Category = snapshot.payload.val();
          category.key = snapshot.key;
          this.categories.push(category);
        });
      });

    this.organizationAccessor
      .getUpdatableOrganizations()
      .snapshotChanges()
      .subscribe((snapshots) => {
        this.organizations = [];
        snapshots.forEach((snapshot) => {
          const organization: Organization = snapshot.payload.val();
          organization.key = snapshot.key;
          this.organizations.push(organization);
        });
      });

    this.providerCategoryAccessor
      .getUpdatableCategories()
      .snapshotChanges()
      .subscribe((categories) => {
        this.providerCategories = [];
        this.allProviderCategories = [];
        categories.forEach((category) => {
          let c: ProviderCategory = category.payload.val();
          c.key = category.key;
          this.providerCategories.push(c);
        });
        this.providerCategories.sort((a, b) => a.Name.localeCompare(b.Name));
        this.allProviderCategories = this.providerCategories;
      });

    const data = this.program;
    if (data) {
      this.selectedCategoryIds = categoryIdsOf(data);
      this.subcategoriesByCategory = subcategoriesByCategoryOf(data);

      this.selectedCategoryIds.forEach((categoryId) => {
        if (!this.subcategoriesByCategory[categoryId]) {
          this.subcategoriesByCategory[categoryId] = [];
        }
        this.fetchSubcategoriesFor(categoryId);
      });

      this.selectedOrganizationId = data.OrganizationId;

      if (data.ProviderCategoryIds) {
        this.selectedProviderCategories = data.ProviderCategoryIds;
      }

      this.coordinates = data.Coordinates;
      this.address = data.Address || '';
      this.call = data.Call || '';
      this.email = data.Email || '';
      this.translatedPrograms['en'].Description = data.Description || '';
      this.translatedPrograms['en'].Summary = data.Summary || '';
      this.website = data.Website || '';
    }
  }

  categoriesSelected() {
    // Fetch options and seed a selection bucket for any newly added category.
    this.selectedCategoryIds.forEach((categoryId) => {
      if (!this.subcategoriesByCategory[categoryId]) {
        this.subcategoriesByCategory[categoryId] = [];
      }
      if (!this.subcategoryOptionsByCategory[categoryId]) {
        this.fetchSubcategoriesFor(categoryId);
      }
    });

    // Drop any category that is no longer selected.
    Object.keys(this.subcategoriesByCategory).forEach((categoryId) => {
      if (!this.selectedCategoryIds.includes(categoryId)) {
        delete this.subcategoriesByCategory[categoryId];
        delete this.subcategoryOptionsByCategory[categoryId];
      }
    });
  }

  fetchSubcategoriesFor(categoryId: string) {
    this.subcategoryAccessor
      .getUpdatableSubcategories(categoryId)
      .snapshotChanges()
      .subscribe((subs) => {
        const options: Subcategory[] = [];
        subs.forEach((s) => {
          const subcategory = s.payload.val();
          subcategory.key = s.key;
          options.push(subcategory);
        });
        this.subcategoryOptionsByCategory[categoryId] = options;
      });
  }

  categoryName(categoryId: string): string {
    const category =
      this.categories && this.categories.find((c) => c.key === categoryId);
    return category ? category.Name : '';
  }

  private sortedKey(ids: string[]): string {
    return (ids || []).slice().sort().join(',');
  }

  generateTimestamp(): Timestamp {
    const changes = [];

    const data = this.program;
    if (data) {
      if (this.address !== data.Address) {
        changes.push(`Address:${data.Address}:${this.address}`);
      }
      if (this.call !== data.Call) {
        changes.push(`Phone:${data.Call}:${this.call}`);
      }
      if (this.translatedPrograms['en'].Description !== data.Description) {
        changes.push(
          `Description:${data.Description}:${this.translatedPrograms['en'].Description}`
        );
      }
      if (this.email !== data.Email) {
        changes.push(`Email:${data.Email}:${this.email}`);
      }
      if (this.translatedPrograms['en'].Summary !== data.Summary) {
        changes.push(
          `Summary:${data.Summary}:${this.translatedPrograms['en'].Summary}`
        );
      }
      if (this.website !== data.Website) {
        changes.push(`Website:${data.Website}:${this.website}`);
      }
      const previousCategoryIds = categoryIdsOf(data);
      if (
        this.sortedKey(previousCategoryIds) !==
        this.sortedKey(this.selectedCategoryIds)
      ) {
        changes.push(
          `Categories:${previousCategoryIds}:${this.selectedCategoryIds}`
        );
      }
      if (this.selectedOrganizationId !== data.OrganizationId) {
        changes.push(
          `Organization:${data.OrganizationId}:${this.selectedOrganizationId}`
        );
      }
      const previousSubcategories = flattenSubcategories(
        subcategoriesByCategoryOf(data)
      );
      const currentSubcategories = flattenSubcategories(
        this.subcategoriesByCategory
      );
      if (
        this.sortedKey(previousSubcategories) !==
        this.sortedKey(currentSubcategories)
      ) {
        changes.push(
          `Subcategories:${previousSubcategories}:${currentSubcategories}`
        );
      }
      if (
        this.selectedProviderCategories.map((category) => category['$key']) &&
        data.ProviderCategoryIds &&
        this.selectedProviderCategories
          .map((category) => category['$key'])
          .sort()
          .join(',') !== data.ProviderCategoryIds.sort().join(',')
      ) {
        changes.push(
          `ProviderCategories:${
            data.ProviderCategoryIds
          }:${this.selectedProviderCategories.map(
            (category) => category['$key']
          )}`
        );
      }
    }

    if (changes.length > 0) {
      return {
        Time: new Date().getTime(),
        Email: this.auth.afAuth.auth.currentUser.email,
        Changes: changes.join(';'),
      };
    } else {
      return {
        Time: new Date().getTime(),
        Email: this.auth.afAuth.auth.currentUser.email,
        Changes: null,
      };
    }
  }

  updateProgram() {
    this.timestamp = this.generateTimestamp();
    this.website = this.formatUrl(this.website.trim());
    this.translatedPrograms['en'].Summary = this.translatedPrograms[
      'en'
    ].Summary.trim();
    this.address = this.address.trim();
    this.call = this.call.trim();
    this.email = this.email.trim();
    this.translatedPrograms['en'].Description = this.translatedPrograms[
      'en'
    ].Description.trim();

    this.languages.forEach((language) => {
      this.translations[language] = {
        summary: this.translatedPrograms[language].Summary,
        description: this.translatedPrograms[language].Description,
        userUpdated: this.translatedPrograms[language].userUpdated,
      };
    });

    // Persist only the subcategory buckets for currently-selected categories,
    // and mirror the legacy singular fields off the primary (first) category.
    const subcategoriesByCategory = pruneSubcategories(
      this.selectedCategoryIds,
      this.subcategoriesByCategory
    );
    const legacy = legacyMirror(
      this.selectedCategoryIds,
      subcategoriesByCategory
    );

    const updatedProgram: Program = {
      Website: this.formatUrl(this.website.trim()),
      Summary: this.translatedPrograms['en'].Summary.trim(),
      Address: this.address.trim(),
      Call: this.call.trim(),
      Email: this.email.trim(),
      Description: this.translatedPrograms['en'].Description.trim(),
      CategoryId: legacy.CategoryId,
      CategoryIds: this.selectedCategoryIds,
      SubcategoriesByCategory: subcategoriesByCategory,
      OrganizationId: this.selectedOrganizationId,
      SubcategoryId: legacy.SubcategoryId,
      SubcategoryIds: legacy.SubcategoryIds,
      Timestamp: this.timestamp,
      ProviderCategoryIds: this.selectedProviderCategories,
    };

    if (
      this.translatedPrograms['en'].Summary.length > 5000 ||
      this.translatedPrograms['en'].Description.length > 5000
    ) {
      this.message =
        'Summary and Description must be less than 5000 characters';
      this.error = true;
    } else {
      const updatablePrograms = this.programAccessor.getUpdatablePrograms();

      updatablePrograms.update(this.id, updatedProgram);
      this.translate.translateProgramsByUserUpdated(
        updatedProgram,
        this.id,
        this.translations
      );

      if (this.address) {
        this.geocoder
          .geocodeNebraskaAddress(this.address)
          .subscribe((location) => {
            updatablePrograms.update(this.id, { Coordinates: location });
            this.translate.addCoordinates(this.id, location);
          });
      }
      this.router.navigate(['/home/program/' + this.id]);
    }
  }

  formatUrl(url: string) {
    if (!url.startsWith('http') && url.length > 0) {
      url = 'http://' + url;
    }
    return url;
  }

  cancel() {
    this.location.back();
  }

  onKeyUp(value: string) {
    this.providerCategories = this.allProviderCategories.filter(option => option.Name.toLowerCase().startsWith(value.toLowerCase()));
    if (!value) {
      this.providerCategories = [...this.allProviderCategories];
    }
  }
}
